import { type FC } from "react";
import { FormProvider, type UseFormReturn } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { LabeledTextInput } from "../LabeledTextInput";
import { LabeledTextarea } from "../LabeledTextarea";

import type { SectionInfoFormValues } from "./types";

import { LMSConnectionIndicator } from "components/materials/section/LMSConnectionIndicator";
import { CourseMenu } from "components/menus/CourseMenu";
import { TermMenu } from "components/menus/TermMenu";
import { showDisconnectConfirmationModal } from "components/modals/DisconnectSectionModal";
import { t } from "i18n/i18n";
import { TooltipTrigger } from "mds/components/TooltipTrigger";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import { selectDesignatedSection, selectLmsType } from "store/selectors";
import { useIsArchived } from "utils/hooks/use-is-archived";
import { isLinkedSection } from "utils/publishing";

type SectionInfoFormProps = {
  className?: string;
  form: UseFormReturn<SectionInfoFormValues>;
  onClose: () => void;
};

export const SectionInfoForm: FC<SectionInfoFormProps> = ({ className, form, onClose }) => {
  const currentSection = useAppSelector(selectDesignatedSection);
  const currentLmsType = useAppSelector(selectLmsType);
  const { canAuthorCourse } = useCapabilities();
  const { isArchived } = useIsArchived();

  const courseId = form.watch("course_id");
  const termId = form.watch("term_id");

  if (!currentSection) {
    return null;
  }

  const onHandleSectionDisconnect = async () => {
    if (!currentSection.lms_section_id || !currentLmsType) return;
    onClose();

    await showDisconnectConfirmationModal({
      sectionId: currentSection.id,
      lmsType: currentLmsType,
    });
  };

  const disabled = isLinkedSection(currentSection) || isArchived(currentSection);

  return (
    <FormProvider {...form}>
      <div
        className={twMerge(
          "flex flex-col text-sm font-medium leading-[1.3125rem] text-black-tint-10",
          className,
        )}
      >
        <div className="flex flex-col gap-2">
          {!disabled && (
            <label className="flex items-center gap-4">
              <div>{t("home.courses.select_course_label")}</div>

              <input type="hidden" {...form.register("course_id")} />

              <div className="inline-block">
                <CourseMenu
                  menuType="menu"
                  selectedCourseId={courseId}
                  showEmptyOption={false}
                  adminAccessibleOnly
                  onCourseChange={(id) => form.setValue("course_id", id, { shouldDirty: true })}
                />
              </div>
            </label>
          )}

          <LabeledTextInput
            {...form.register("title", { required: t("error.field_required"), disabled })}
            label={t("glossary.section_title")}
            required
          />

          {!disabled && (
            <label className="flex items-center gap-4">
              <div>{t("home.terms.select_term_label")}</div>

              <input type="hidden" {...form.register("term_id")} />

              <div className="inline-block">
                <TermMenu
                  menuType="menu"
                  selectedTermId={termId}
                  showEmptyOption={false}
                  onTermChange={(id) => form.setValue("term_id", id, { shouldDirty: true })}
                />
              </div>
            </label>
          )}

          <LabeledTextarea
            {...form.register("description", { disabled })}
            label={t("glossary.section_description")}
            minRows={3}
            placeholder={t("home.placeholder.section_description")}
          />

          <LabeledTextInput
            wrapperClassName={
              !canAuthorCourse && !currentSection.external_section_id ? "hidden" : undefined
            }
            {...form.register("external_section_id", { disabled: !canAuthorCourse || disabled })}
            label={t("settings.external_section_id")}
            postLabel={<TooltipTrigger content={t("settings.editable_admins_only")} />}
            required
          />

          <div className="flex flex-col gap-1">
            <h3 className="mt-1 text-xs font-normal leading-[1.25rem] text-black-tint-20">
              {t("settings.lms_integration")}
            </h3>

            <div className="flex flex-row items-center justify-between">
              <LMSConnectionIndicator />

              {currentSection.lms_section_id && (
                <SquareButton
                  disabled={disabled}
                  kind="caution"
                  size="xs"
                  onClick={onHandleSectionDisconnect}
                >
                  {t("settings.disconnect_section")}
                </SquareButton>
              )}
            </div>
          </div>
        </div>
      </div>
    </FormProvider>
  );
};
